<template>
    <b-form-row v-if="patient">
        <b-col>
            <b-form-row class="align-items-center">
                <b-col cols="4">
                    <SearchComponent v-model="selectedAllergy" :label="`Allergy Search`"
                        search-u-r-l="/Allergy/GetGSDDAllergies" :disabled="disable" clearable />
                </b-col>
                <b-col cols="3">
                    <b-form-group label="Reaction:">
                        <b-input v-model="reaction" maxlength="100" :disabled="disable" />
                    </b-form-group>
                </b-col>
                <b-col cols="3">
                    <b-form-group label="Origin:">
                        <b-form-select v-model="newAllergyOrigin" :options="originOptions" :disabled="disable" />
                    </b-form-group>
                </b-col>
                <b-col cols="2">
                    <checkbox v-model="patient.nkda" :disabled="(disable || disableNkda)" @input="nkdaChanged">
                        NKDA
                    </checkbox>
                </b-col>
            </b-form-row>

            <b-button @click="addAllergy" v-if="!disable" variant="success">
                Add Allergy
            </b-button>

            <b-table v-if="!!patientAllergies.length" sort-icon-left no-provider-sorting
                :items="patientAllergies" :fields="allergyTableFields"
                :sort-by.sync="allergySortBy" :sort-desc.sync="allergySortDesc"
                :sort-compare-locale="sortLocale" :sort-compare-options="sortOptions">
                <template #cell(created)="data">
                    {{ data.value | formatDate("LLL") }}
                </template>
                <template #cell(actions)="data">
                    <b-button variant="danger" size="sm" :disabled="disable" @click="RemoveAllergy(data.item)">Remove</b-button>
                </template>
            </b-table>

            <div v-if="patient && patient.id && requireRemovalReason" class="mt-2">
                <b-button variant="link" size="sm" class="px-0" @click="toggleArchived">
                    {{ showArchived ? 'Hide' : 'Show' }} removed allergies{{ archivedCountLabel }}
                </b-button>
                <b-table v-if="showArchived && archivedAllergies.length > 0" small sort-icon-left no-provider-sorting
                    :items="archivedAllergies" :fields="archivedTableFields"
                    :sort-by.sync="archivedSortBy" :sort-desc.sync="archivedSortDesc"
                    :sort-compare-locale="sortLocale" :sort-compare-options="sortOptions">
                    <template #cell(removed)="data">
                        {{ data.value | formatDate("LLL") }}
                    </template>
                </b-table>
                <div v-else-if="showArchived" class="text-muted small">No removed allergies.</div>
            </div>

            <RemovalReasonModal ref="reasonModal" />
        </b-col>
    </b-form-row>
</template>

<script lang="ts">
import { Component, Watch, Prop, Vue, Ref } from 'vue-property-decorator'
import { DatePicker } from "element-ui";
import axios from 'axios';
import { NotificationOptions } from "@/util/NotificationOptionsPresets"
import { Patient } from '@/models/Patient';
import SearchComponent from '@/components/SearchComponent.vue';
import NewClearSaveButtons from '@/components/NewClearSaveButtons.vue';
import AddressFormFields from '@/components/AddressFormFields.vue';
import Checkbox from "@/components/Inputs/Checkbox.vue";
import RemovalReasonModal from '@/components/Patient/RemovalReasonModal.vue';
import { GSDDPatientAllergy, AllergyRemovalReason, compareAllergiesByName, allergyTypeLabel, allergySortLocale, allergySortOptions } from "@/models/GSDD";
import { MacOrigin, macOriginLabel } from "@/models/MacOrigin";
import { enumToOptions } from "@/util/EnumOptions";
import { BvTableFieldArray } from "bootstrap-vue";

@Component({
    name: "GSDDAllergyComponent",
    components: {
        SearchComponent,
        NewClearSaveButtons,
        AddressFormFields,
        Checkbox,
        RemovalReasonModal,
        [DatePicker.name]: DatePicker
    }
})
export default class GSDDAllergyComponent extends Vue {
    @Prop({ default: null }) private patient!: Patient;
    @Prop({ default: false }) private disable!: boolean
    @Prop({ default: () => [] }) private value!: GSDDPatientAllergy[];
    @Prop({ default: null }) private defaultOrigin!: MacOrigin | null;

    @Ref('reasonModal') private reasonModal!: RemovalReasonModal;

    //private patientAllergies: GSDDPatientAllergy[] = []
    private selectedAllergy: GSDDPatientAllergy = new GSDDPatientAllergy();
    private reaction: string = "";
    // On the class, not on selectedAllergy: that class declares no fields, so Vue 2 can't track a late key.
    protected newAllergyOrigin: MacOrigin | null = null;
    protected readonly originOptions = enumToOptions(MacOrigin, { includeNone: true });
    protected readonly originLabel = macOriginLabel;
    protected readonly allergyTypeLabel = allergyTypeLabel;

    // Sort state is held here rather than left to each b-table's own local state: both tables sit
    // behind a v-if, so a list that empties and repopulates would otherwise silently drop the
    // column the user picked back to the default. Defaults reapply on a fresh mount, so a page
    // reload still lands on alphabetical.
    protected allergySortBy = "allergyName";
    protected allergySortDesc = false;
    protected archivedSortBy = "removed";
    protected archivedSortDesc = true;

    // b-table re-sorts the items it's given, so without these it would order rows by the viewer's
    // runtime locale instead of the collation compareAllergiesByName already applied.
    protected readonly sortLocale = allergySortLocale;
    protected readonly sortOptions = allergySortOptions;

    protected requireRemovalReason = false;
    protected showArchived = false;
    protected archivedAllergies: GSDDPatientAllergy[] = [];
    // Loaded up front (the toggle advertises a count) and cached until the patient changes.
    private archivedLoaded = false;

    // Collected removal reasons for allergies removed since the last save; read by the parent
    // form when it POSTs the reconcile so the backend can persist each reason. Public by design.
    public removalReasons: AllergyRemovalReason[] = [];

    get patientAllergies(): GSDDPatientAllergy[] {
        return this.value;
    }

    set patientAllergies(val: GSDDPatientAllergy[]) {
        this.$emit('input', val);
    }

    get disableNkda(): boolean {
        return this.patientAllergies.length > 0;
    }

    // Only advertise a count once one is actually known. An unloaded list is empty, so rendering it
    // unconditionally makes a patient with removed allergies read the same as one with none.
    get archivedCountLabel(): string {
        return this.archivedLoaded ? ` (${this.archivedAllergies.length})` : "";
    }

    get allergyTableFields(): BvTableFieldArray {
        return [
            { key: "allergyName", label: "Allergen", sortable: true },
            { key: "allergyType", label: "Type", sortable: true, formatter: this.allergyTypeLabel, sortByFormatted: true },
            { key: "reaction", label: "Reaction", sortable: true },
            { key: "origin", label: "Origin", sortable: true, formatter: this.originLabel, sortByFormatted: true },
            { key: "created", label: "Added Date", sortable: true, sortByFormatted: this.dateSortValue },
            { key: "actions", label: "", thClass: "actions-col" },
        ];
    }

    get archivedTableFields(): BvTableFieldArray {
        return [
            { key: "allergyName", label: "Allergen", sortable: true },
            { key: "reaction", label: "Reaction", sortable: true },
            { key: "origin", label: "Origin", sortable: true, formatter: this.originLabel, sortByFormatted: true },
            { key: "removed", label: "Removed Date", sortable: true, sortByFormatted: this.dateSortValue },
            { key: "removedBy", label: "Removed By", sortable: true },
            { key: "removalReason", label: "Reason", sortable: true },
        ];
    }

    // Sort key only — the cell still renders the raw date through the formatDate filter. Mixed Date
    // objects (fresh adds) and ISO strings (loaded rows) don't compare as strings, so both collapse
    // to a timestamp.
    protected dateSortValue(value?: Date | string | null): number {
        return value ? new Date(value).getTime() : 0;
    }

    created() {
        this.newAllergyOrigin = this.defaultOrigin;
        this.getPatientGSDDAllergies();
        this.fetchRemovalReasonConfig();
    }

    async fetchRemovalReasonConfig() {
        try {
            const resp = await axios.get<boolean>('/Configuration/RequireReasonForPatientMacRemoval');
            this.requireRemovalReason = resp.data === true;
        } catch (err) {
            // Default to prompting on a transient failure: the server enforces the rule independently,
            // so skipping the prompt would only produce a confusing 400 the user can't act on.
            console.error("Error loading removal-reason config", err);
            this.requireRemovalReason = true;
        }

        // This config is what renders the toggle, and the toggle shows a count, so the list has to
        // be in hand before the user can read it.
        if (this.requireRemovalReason) await this.fetchArchivedAllergies();
    }

    async toggleArchived() {
        this.showArchived = !this.showArchived;
        // Normally already loaded; this retries an eager fetch that failed.
        if (this.showArchived && !this.archivedLoaded) await this.fetchArchivedAllergies();
    }

    async fetchArchivedAllergies() {
        const requestedPatientId = this.patient?.id;
        if (!requestedPatientId) return;
        try {
            const resp = await axios.get<GSDDPatientAllergy[]>(`/Allergy/GetArchivedGSDDAllergiesForPatient/${requestedPatientId}`);
            // Now that this fires on every patient change rather than on an explicit expand, a switch
            // mid-flight is reachable — without this the previous patient's removed list would land.
            if (this.patient?.id !== requestedPatientId) return;
            this.archivedAllergies = resp.data;
            this.archivedLoaded = true;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    getPatientGSDDAllergies() {
        if (!this.patient?.id) return;

        axios.get(`/Allergy/GetGSDDAllergiesForPatient/${this.patient.id}`)
            .then(response => {
                this.patientAllergies = response.data.sort(compareAllergiesByName);
            }).catch(error => {
                console.error("Error while loading allergies", { error, response: error?.response });
                this.$notification(NotificationOptions.error(error));
            });
    }

    @Watch('patient.id') onPatientChanged(value: Patient | null, oldValue: Patient | null) {
        this.clearComponent();
        this.getPatientGSDDAllergies();
        // Drop the previous patient's archived list, then reload so the count is right for this one.
        this.archivedLoaded = false;
        this.archivedAllergies = [];
        this.showArchived = false;
        if (this.requireRemovalReason) this.fetchArchivedAllergies();
    }

    nkdaChanged(val: boolean) {
        if (val) {
            this.patientAllergies = [];
        }
    }

    addAllergy() {
        if (!this.selectedAllergy?.allergyName) {
            this.$notification(NotificationOptions.error("Please select an Allergy."));
            return;
        }

        const alreadyAdded = this.patientAllergies.some(a =>
            a.allergyType === this.selectedAllergy.allergyType && a.allergyId === this.selectedAllergy.allergyId);
        if (alreadyAdded) {
            this.$notification(NotificationOptions.error("This Allergy has already been added."));
            return;
        }

        // Build the row complete before unshifting so the array Observer sees origin from the start.
        this.patientAllergies.unshift(new GSDDPatientAllergy({
            ...this.selectedAllergy,
            patientId: this.patient.id as number,
            reaction: this.reaction,
            created: new Date(),
            origin: this.newAllergyOrigin,
        } as GSDDPatientAllergy));
        this.patient.nkda = false;
        this.reaction = "";
        this.newAllergyOrigin = this.defaultOrigin;
    }

    async RemoveAllergy(allergy: any) {
        // Only removing an allergy that is already persisted needs a reason; a not-yet-saved
        // addition (no created date) can be dropped freely.
        if (this.requireRemovalReason && this.patient?.id && allergy.created) {
            const reason = await this.reasonModal.prompt({
                itemLabel: allergy.allergyName,
                required: true,
                title: 'Remove Allergy',
            });
            if (reason === null) return;

            this.removalReasons = [
                ...this.removalReasons.filter(r => !(r.allergyType === allergy.allergyType && r.allergyId === allergy.allergyId && r.reaction === allergy.reaction)),
                { allergyType: allergy.allergyType, allergyId: allergy.allergyId, reaction: allergy.reaction, reason },
            ];
        }

        this.patientAllergies = this.patientAllergies.filter(
            item => !(item.allergyType === allergy.allergyType && item.allergyId === allergy.allergyId && item.reaction === allergy.reaction));
    }

    clearComponent() {
        this.patientAllergies = new Array<GSDDPatientAllergy>();
        this.selectedAllergy = new GSDDPatientAllergy();
        this.reaction = "";
        this.newAllergyOrigin = this.defaultOrigin;
        this.removalReasons = [];
    }

}
</script>

<style scoped>
/* b-table renders the field's thClass onto the <th>, which scoped styles only reach via :deep. */
:deep(.actions-col) {
    width: 110px;
}
</style>
